<script setup>
import { ref } from 'vue';
import { RouterLink, useRoute } from 'vue-router';

const route = useRoute();
const activeSection = ref('dashboard');

const sections = [
  { id: 'dashboard', name: 'Tableau de bord', icon: 'dashboard' },
  { id: 'orders', name: 'Mes commandes', icon: 'shopping_bag' },
  { id: 'giftcards', name: 'Mes cartes cadeaux', icon: 'card_giftcard' },
  { id: 'profile', name: 'Mon profil', icon: 'person' },
  { id: 'security', name: 'Sécurité', icon: 'lock' },
];

const stats = ref({
  totalOrders: 12,
  deliveredOrders: 10,
  giftCards: 3,
});

const recentOrders = ref([
  {
    id: '2024-001',
    name: 'Abonnement Netflix',
    variant: '1 mois',
    price: 2500,
    status: 'delivered',
    date: '2024-01-10',
  },
  {
    id: '2024-002',
    name: 'Abonnement Spotify',
    variant: '3 mois',
    price: 9000,
    status: 'delivered',
    date: '2024-01-08',
  },
  {
    id: '2024-003',
    name: 'Carte Cadeau Netflix',
    variant: '',
    price: 5000,
    status: 'pending',
    date: '2024-01-12',
  },
]);

const giftCards = ref([
  {
    id: 1,
    name: 'Netflix',
    balance: 15000,
    color: 'from-red-500 to-red-700',
    icon: 'play_circle',
  },
  {
    id: 2,
    name: 'Spotify',
    balance: 8000,
    color: 'from-green-500 to-green-700',
    icon: 'music_note',
  },
]);

const statusLabels = {
  delivered: 'Livré',
  pending: 'En traitement',
  cancelled: 'Annulé',
};

const statusClasses = {
  delivered: 'text-emerald-500',
  pending: 'text-amber-500',
  cancelled: 'text-red-500',
};
</script>

<template>
  <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

    <!-- Breadcrumb -->
    <nav aria-label="Breadcrumb" class="flex items-center text-sm text-slate-500 dark:text-slate-400 mb-8">
      <RouterLink class="hover:text-primary transition-colors" to="/store">Store</RouterLink>
      <span class="mx-2">»</span>
      <span class="font-medium text-slate-900 dark:text-slate-200">Mon Compte</span>
    </nav>

    <div class="grid grid-cols-1 lg:grid-cols-4 gap-8">

      <!-- Sidebar Navigation -->
      <div class="lg:col-span-1">
        <div class="bg-white dark:bg-card-dark rounded-2xl p-6 border border-slate-200 dark:border-slate-800 sticky top-28">
          <!-- User Profile Summary -->
          <div class="flex items-center gap-4 mb-6 pb-6 border-b border-slate-200 dark:border-slate-700">
            <div class="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white text-2xl font-bold">
              JD
            </div>
            <div>
              <h3 class="font-bold">Jean Dupont</h3>
              <p class="text-sm text-slate-500">jean.dupont@email.com</p>
            </div>
          </div>

          <!-- Navigation Menu -->
          <nav class="space-y-2">
            <button
              v-for="section in sections"
              :key="section.id"
              @click="activeSection = section.id"
              class="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-colors"
              :class="activeSection === section.id ? 'bg-primary/10 text-primary' : 'hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400'"
            >
              <span class="material-icons-outlined text-xl">{{ section.icon }}</span>
              {{ section.name }}
            </button>
            <RouterLink to="/store" class="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-xl text-slate-600 dark:text-slate-400 transition-colors">
              <span class="material-icons-outlined text-xl">logout</span>
              Déconnexion
            </RouterLink>
          </nav>
        </div>
      </div>

      <!-- Main Content -->
      <div class="lg:col-span-3 space-y-8">

        <!-- Welcome Section -->
        <div class="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl p-8 border border-primary/20">
          <h1 class="text-3xl font-extrabold mb-2">Bienvenue, Jean !</h1>
          <p class="text-slate-600 dark:text-slate-400">Gérez vos commandes, vos cartes cadeaux et vos préférences compte.</p>
        </div>

        <!-- Stats Cards -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div class="bg-white dark:bg-card-dark rounded-2xl p-6 border border-slate-200 dark:border-slate-800">
            <div class="flex items-center gap-4">
              <div class="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center">
                <span class="material-icons-outlined text-primary text-2xl">shopping_bag</span>
              </div>
              <div>
                <p class="text-3xl font-extrabold">{{ stats.totalOrders }}</p>
                <p class="text-sm text-slate-500">Commandes totales</p>
              </div>
            </div>
          </div>
          <div class="bg-white dark:bg-card-dark rounded-2xl p-6 border border-slate-200 dark:border-slate-800">
            <div class="flex items-center gap-4">
              <div class="w-14 h-14 bg-emerald-500/10 rounded-xl flex items-center justify-center">
                <span class="material-icons-outlined text-emerald-500 text-2xl">check_circle</span>
              </div>
              <div>
                <p class="text-3xl font-extrabold">{{ stats.deliveredOrders }}</p>
                <p class="text-sm text-slate-500">Livré</p>
              </div>
            </div>
          </div>
          <div class="bg-white dark:bg-card-dark rounded-2xl p-6 border border-slate-200 dark:border-slate-800">
            <div class="flex items-center gap-4">
              <div class="w-14 h-14 bg-secondary/10 rounded-xl flex items-center justify-center">
                <span class="material-icons-outlined text-secondary text-2xl">card_giftcard</span>
              </div>
              <div>
                <p class="text-3xl font-extrabold">{{ stats.giftCards }}</p>
                <p class="text-sm text-slate-500">Cartes cadeaux</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Recent Orders -->
        <section class="bg-white dark:bg-card-dark rounded-2xl p-8 border border-slate-200 dark:border-slate-800">
          <div class="flex items-center justify-between mb-6">
            <h2 class="text-xl font-bold">Commandes récentes</h2>
            <RouterLink to="/account?section=orders" class="text-primary font-medium text-sm hover:underline flex items-center gap-1">
              Voir tout <span class="material-icons-outlined text-sm">chevron_right</span>
            </RouterLink>
          </div>

          <div class="space-y-4">
            <div v-for="order in recentOrders" :key="order.id" class="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl flex items-center justify-between">
              <div class="flex items-center gap-4">
                <div class="w-12 h-12 rounded-lg flex items-center justify-center" :class="order.status === 'delivered' ? 'bg-emerald-500/10' : 'bg-amber-500/10'">
                  <span class="material-icons-outlined" :class="order.status === 'delivered' ? 'text-emerald-500' : 'text-amber-500'">
                    {{ order.status === 'delivered' ? 'check_circle' : 'pending' }}
                  </span>
                </div>
                <div>
                  <p class="font-semibold">Commande #{{ order.id }}</p>
                  <p class="text-sm text-slate-500">{{ order.name }}{{ order.variant ? ' • ' + order.variant : '' }}</p>
                </div>
              </div>
              <div class="text-right">
                <p class="font-bold text-primary">{{ order.price.toLocaleString() }} CFA</p>
                <p class="text-xs font-medium" :class="statusClasses[order.status]">{{ statusLabels[order.status] }}</p>
              </div>
            </div>
          </div>
        </section>

        <!-- Gift Cards Section -->
        <section class="bg-white dark:bg-card-dark rounded-2xl p-8 border border-slate-200 dark:border-slate-800">
          <div class="flex items-center justify-between mb-6">
            <h2 class="text-xl font-bold">Mes Cartes Cadeaux</h2>
            <RouterLink to="/giftcards" class="text-primary font-medium text-sm hover:underline">Acheter une carte</RouterLink>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div v-for="card in giftCards" :key="card.id" class="relative overflow-hidden rounded-2xl p-6 text-white" :class="card.color">
              <div class="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
              <div class="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full -ml-12 -mb-12"></div>
              <div class="relative">
                <div class="flex items-center gap-2 mb-4">
                  <span class="material-icons text-3xl">{{ card.icon }}</span>
                  <span class="font-bold text-lg">{{ card.name }}</span>
                </div>
                <p class="text-sm opacity-80 mb-4">Carte Cadeau</p>
                <div class="flex justify-between items-end">
                  <div>
                    <p class="text-xs opacity-70">Solde</p>
                    <p class="text-2xl font-bold">{{ card.balance.toLocaleString() }} CFA</p>
                  </div>
                  <button class="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl text-sm font-medium transition-colors">
                    Recharger
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

      </div>
    </div>

  </div>
</template>
